/**
 * htmlToPlainText - Convert rich-text HTML into a readable plain-text string.
 *
 * Used by rich-text compose surfaces (e.g. support ticket replies) to derive the
 * `body_text` plain-text fallback that accompanies `body_html`. The plain-text
 * part powers plain-text email bodies, notification previews, and accessibility
 * fallbacks where rendered HTML isn't appropriate.
 *
 * Block-level elements and <br> become line breaks so the text stays readable;
 * runs of blank lines are collapsed.
 *
 * Browser-only: relies on DOMParser, so it must run in the SPA (or a jsdom test),
 * never in a backend/Lambda context.
 */
export function htmlToPlainText(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc
    .querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, blockquote, tr')
    .forEach((el) => el.append('\n'));
  // Break a list off its parent item's inline text so a nested list's first
  // entry doesn't run onto the end of the enclosing line ("a" + "b" → "ab").
  doc.querySelectorAll('ul, ol').forEach((el) => el.prepend('\n'));
  const text = doc.body.textContent ?? '';
  return text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * isRichTextEmpty - True when rich-text HTML has no visible text content.
 *
 * A TipTap editor that the user focuses and leaves (or types into and clears)
 * emits markup like `<p></p>` that is non-empty as a string but renders nothing.
 * Use this before persisting an optional rich-text field so an empty editor is
 * stored as undefined/null rather than empty markup.
 */
export function isRichTextEmpty(html: string | null | undefined): boolean {
  return !html || htmlToPlainText(html).length === 0;
}
